'use es6';

import { Fragment } from 'react';
import UniversalSidebarCard from './UniversalSidebarCard';
import UIDragHandle from 'UIComponents/draggable/UIDragHandle';
import { getEmptyImage } from 'react-dnd-html5-backend';
import UIExpandableSection from 'UIComponents/accordion/UIExpandableSection';
import styled, { css } from 'styled-components';
import { GYPSUM, CALYPSO } from 'HubStyleTokens/colors';
import memoize from 'transmute/memoize';
import identity from 'transmute/identity';
import DnDListWrapper from 'customer-data-ui-utilities/dnd/DnDListWrapper';
import PropTypes from 'prop-types';
import useComponentDidMount from './hooks/useComponentDidMount';
import { jsx as _jsx } from "react/jsx-runtime";
const BorderlessHeader = styled(UIExpandableSection.defaultProps.Header).withConfig({
  displayName: "UniversalSidebarCardDnDWrapper__BorderlessHeader",
  componentId: "sc-1lan2p4-0"
})(["border-top-width:0;"]);
const CardDraggingInFront = css(["border-color:", ";border-style:solid;border-width:2px 0 0 0;"], CALYPSO);
const CardDraggingBehind = css(["border-color:", ";border-style:solid;border-width:0 0 2px 0;"], CALYPSO);
const CardBeingDragged = css(["background-color:", ";"], GYPSUM);
const CardWrapper = styled.section.withConfig({
  displayName: "UniversalSidebarCardDnDWrapper__CardWrapper",
  componentId: "sc-1lan2p4-1"
})(["border-color:", ";", " ", " ", ""], GYPSUM, props => props.isDragging && CardBeingDragged, props => props.draggingInFront && CardDraggingInFront, props => props.draggingBehind && CardDraggingBehind);
const getHeaderComponent = isBordered => isBordered ? UIExpandableSection.defaultProps.Header : BorderlessHeader;
const DragHandle = memoize(connectDragSource => handleProps => connectDragSource( /*#__PURE__*/_jsx("div", {
  children: /*#__PURE__*/_jsx(UIDragHandle, Object.assign({}, handleProps))
})));
const CardHeader = memoize((connectDragSource, isBordered) => headerProps => {
  const HeaderComponent = getHeaderComponent(isBordered);
  return connectDragSource( /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(HeaderComponent, Object.assign({}, headerProps))
  }));
});
const UniversalSidebarCardDnDWrapper = ({
  isBordered,
  // UniversalSidebarCard props
  Actions,
  Content,
  cardProps,
  data,
  draggable,
  isDragging,
  objectId,
  objectType,
  Title,
  // drag and drop props
  connectDragPreview,
  connectDragSource,
  connectDropTarget,
  draggingFromIndex,
  draggingToIndex,
  index
}) => {
  useComponentDidMount(() => {
    connectDragPreview(getEmptyImage(), {
      captureDraggingState: true
    });
  });
  if (!draggable) {
    return /*#__PURE__*/_jsx(UniversalSidebarCard, {
      objectType: objectType,
      objectId: objectId,
      Title: Title,
      Actions: Actions,
      Content: Content,
      cardProps: cardProps,
      data: data,
      Header: getHeaderComponent(isBordered),
      isDragging: false,
      draggable: false
    });
  }
  const isCurrentDropTarget = draggingToIndex === index;
  return /*#__PURE__*/_jsx(Fragment, {
    children: connectDropTarget( /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(CardWrapper, {
        isDragging: isDragging,
        draggingInFront: isCurrentDropTarget && draggingToIndex < draggingFromIndex,
        draggingBehind: isCurrentDropTarget && draggingToIndex > draggingFromIndex,
        children: /*#__PURE__*/_jsx(UniversalSidebarCard, {
          objectType: objectType,
          objectId: objectId,
          Title: Title,
          Actions: Actions,
          Content: Content,
          cardProps: cardProps,
          data: data,
          DragHandle: DragHandle(connectDragSource),
          Header: CardHeader(connectDragSource, isBordered),
          isDragging: isDragging,
          draggable: draggable
        })
      })
    }))
  });
};
UniversalSidebarCardDnDWrapper.propTypes = {
  Content: PropTypes.func.isRequired,
  Title: PropTypes.func.isRequired,
  Actions: PropTypes.func,
  cardProps: PropTypes.object,
  data: PropTypes.object.isRequired,
  objectType: PropTypes.string.isRequired,
  objectId: PropTypes.string.isRequired,
  isDragging: PropTypes.bool,
  isBordered: PropTypes.bool,
  draggable: PropTypes.bool,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  connectDragPreview: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  draggingFromIndex: PropTypes.number,
  draggingToIndex: PropTypes.number
};

// for clarity, the DnDListWrapper is meant to wrap individual items in a drag and drop list (like a card), not the list itself
const makeUniversalSidebarCardDnDWrapper = memoize(namespace => {
  const wrapper = DnDListWrapper(namespace, identity)(UniversalSidebarCardDnDWrapper);
  wrapper.displayName = `UniversalSidebarCardDnDWrapper(${namespace})`;
  return wrapper;
});
export { UniversalSidebarCardDnDWrapper, makeUniversalSidebarCardDnDWrapper };